import { useMemo, useEffect, useCallback } from 'react'
import { eachDayOfInterval, format, addMonths, subMonths, isSameMonth } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useCalendarStore } from '../stores/calendarStore'
import { useAuthStore } from '../stores/authStore'
import { useFamilyStore } from '../stores/familyStore'

type Location = 'home_a' | 'home_b' | null

export function useCalendar() {
  // Selektory zustand - każda wartość osobno
  const entries = useCalendarStore(state => state.entries)
  const children = useCalendarStore(state => state.children)
  const selectedChildId = useCalendarStore(state => state.selectedChildId)
  const selectedHome = useCalendarStore(state => state.selectedHome)
  const fetchEntries = useCalendarStore(state => state.fetchEntries)
  const fetchChildren = useCalendarStore(state => state.fetchChildren)
  const addEntry = useCalendarStore(state => state.addEntry)
  const updateEntry = useCalendarStore(state => state.updateEntry)
  const deleteEntry = useCalendarStore(state => state.deleteEntry)
  const setSelectedChild = useCalendarStore(state => state.setSelectedChild)
  const setSelectedHome = useCalendarStore(state => state.setSelectedHome)
  const setStoreCurrentDate = useCalendarStore(state => state.setCurrentDate)
  const setStoreViewMode = useCalendarStore(state => state.setViewMode)
  const storeCurrentDate = useCalendarStore(state => state.currentDate)
  const storeViewMode = useCalendarStore(state => state.viewMode)
  const isLoading = useCalendarStore(state => state.isLoading)
  const error = useCalendarStore(state => state.error)

  const { user } = useAuthStore()

  const currentDate = useMemo(() => new Date(storeCurrentDate), [storeCurrentDate])
  const viewMode = storeViewMode

  // Oblicz pełny zakres dni do wyświetlenia w kalendarzu (z ciągłością)
  const fullCalendarRange = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const firstDayWeekday = firstDayOfMonth.getDay()
    const adjustedFirstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1
    const firstDisplayDay = new Date(firstDayOfMonth)
    firstDisplayDay.setDate(firstDayOfMonth.getDate() - adjustedFirstDayWeekday)
    const lastDisplayDay = new Date(lastDayOfMonth)
    const lastDayWeekday = lastDayOfMonth.getDay()
    const adjustedLastDayWeekday = lastDayWeekday === 0 ? 6 : lastDayWeekday - 1
    const remainingDays = 6 - adjustedLastDayWeekday
    lastDisplayDay.setDate(lastDayOfMonth.getDate() + remainingDays)
    return {
      start: firstDisplayDay,
      end: lastDisplayDay
    }
  }, [currentDate])

  // Pobierz dzieci dla zalogowanego użytkownika
  const familyStore = useFamilyStore()

  // Funkcja do tworzenia tymczasowych dzieci, jeśli nie ma żadnych w bazie danych
  const createTemporaryChildIfNeeded = useCallback(() => {
    if (children.length === 0 && user && !isLoading) {
      // Dodaj tymczasowe dziecko do stanu
      const tempChild = {
        id: 'temp-child-id',
        family_id: 'temporary-family-id',
        name: 'Wprowadź dzieci',
        birth_date: null,
        created_at: new Date().toISOString()
      }
      // Ustaw tymczasowe dziecko tylko jeśli nie ma wybranego dziecka
      if (!selectedChildId) {
        setSelectedChild('temp-child-id')
      }
      return [tempChild]
    }
    return children
  }, [children, user, isLoading, setSelectedChild, selectedChildId])

  // Pobierz wpisy dla pełnego zakresu dni wyświetlanych w kalendarzu
  useEffect(() => {
    const childId = selectedChildId || 'temp-child-id'
    if (childId && user && !isLoading) {
      const startDate = format(fullCalendarRange.start, 'yyyy-MM-dd')
      const endDate = format(fullCalendarRange.end, 'yyyy-MM-dd')
      fetchEntries(childId, startDate, endDate)
    }
  }, [selectedChildId, fullCalendarRange, user, fetchEntries, isLoading])

  useEffect(() => {
    const loadChildren = async () => {
      if (user && !isLoading && familyStore.initialized) {
        // Jeśli mamy rodzinę w store – użyj jej ID bez dodatkowego odpytywania
        const existingFamilyId = familyStore.family?.id
        if (existingFamilyId) {
          fetchChildren(existingFamilyId)
          return
        }
        try {
          const familyId = await familyStore.getUserFamilyId()
          if (familyId) {
            fetchChildren(familyId)
          } else {
            fetchChildren('temporary-family-id')
          }
        } catch (error) {
          fetchChildren('temporary-family-id')
        }
      }
    }
    loadChildren()
  }, [user, fetchChildren, isLoading, familyStore.initialized, familyStore.family?.id])

  // Używamy tymczasowych dzieci, jeśli nie ma żadnych w bazie
  const displayChildren = useMemo(() => {
    return createTemporaryChildIfNeeded()
  }, [children, createTemporaryChildIfNeeded])

  const calendarDays = useMemo(() => {
    // Generujemy wszystkie dni do wyświetlenia w kalendarzu
    const allDays = eachDayOfInterval({
      start: fullCalendarRange.start,
      end: fullCalendarRange.end
    })
    return allDays.map(day => {
      const dateString = format(day, 'yyyy-MM-dd')
      const dayEntries = entries.filter(entry => entry.date === dateString)
      
      // Zapisujemy jeden wpis dzienny; jeśli jest, bierzemy pierwszy
      const dayEntry = dayEntries[0]
      
      return {
        date: day,
        dateString,
        isCurrentMonth: isSameMonth(day, currentDate),
        location: dayEntry?.location || null,
        comment: dayEntry?.comment || null
      }
    })
  }, [fullCalendarRange, entries, currentDate])

  const monthName = useMemo(() => {
    return format(currentDate, 'LLLL yyyy', { locale: pl })
  }, [currentDate])

  const nextMonth = useCallback(() => {
    setStoreCurrentDate(format(addMonths(currentDate, 1), 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const prevMonth = useCallback(() => {
    setStoreCurrentDate(format(subMonths(currentDate, 1), 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const nextWeek = useCallback(() => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() + 7)
    setStoreCurrentDate(format(newDate, 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const prevWeek = useCallback(() => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() - 7)
    setStoreCurrentDate(format(newDate, 'yyyy-MM-dd'))
  }, [currentDate, setStoreCurrentDate])

  const updateCalendarEntry = useCallback(async (date: string, _segment: 'day', location: Location, comment?: string) => {
    const childId = selectedChildId || 'temp-child-id'
    if (!childId || !user) return
    // Pobierz ID rodziny użytkownika
    const familyId = await familyStore.getUserFamilyId() || 'temporary-family-id'
    // Znajdź istniejący wpis
    const existingEntry = entries.find(entry => entry.date === date)
    // Jeśli location jest null, usuń wpis
    if (location === null && existingEntry) {
      await deleteEntry(existingEntry.id)
      return
    }
    // Jeśli wpis istnieje, zaktualizuj go
    if (existingEntry) {
      await updateEntry(existingEntry.id, {
        location: location as 'home_a' | 'home_b',
        comment: comment || existingEntry.comment
      })
      return
    }
    // W przeciwnym razie dodaj nowy wpis
    if (location) {
      await addEntry({
        child_id: childId,
        family_id: familyId,
        date,
        location: location as 'home_a' | 'home_b',
        comment: comment || null,
        created_by: user.id
      })
    }
  }, [selectedChildId, user, familyStore, entries, deleteEntry, updateEntry, addEntry])

  return {
    currentDate,
    monthName,
    calendarDays,
    viewMode,
    entries,
    children: displayChildren,
    selectedChildId: selectedChildId || 'temp-child-id',
    selectedHome,
    isLoading,
    error,
    nextMonth,
    prevMonth,
    nextWeek,
    prevWeek,
    setViewMode: setStoreViewMode,
    setSelectedChild,
    setSelectedHome,
    updateCalendarEntry
  }
} 